import { PerformanceMetrics, MemoryInfo, WebVitals, PowerConsumptionInfo } from '../types';

/**
 * 获取内存使用信息
 */
export function getMemoryInfo(): MemoryInfo | null {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      totalJSHeapSize: memory.totalJSHeapSize
    };
  }
  return null;
}

/**
 * 获取页面加载时间
 */
export function getLoadTime(): number {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navigation) {
    return navigation.loadEventEnd - navigation.loadEventStart;
  }
  return 0;
}

/**
 * 获取DOM内容加载时间
 */
export function getDOMContentLoadedTime(): number {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navigation) {
    return navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart;
  }
  return 0;
}

/**
 * 计算总阻塞时间 (TBT)
 */
export function calculateTBT(): number {
  const longTasks = performance.getEntriesByType('longtask');
  let totalBlockingTime = 0;
  
  longTasks.forEach(task => {
    const longTask = task as any;
    // 只计算超过50ms的任务
    if (longTask.duration > 50) {
      totalBlockingTime += longTask.duration - 50;
    }
  });
  
  return totalBlockingTime;
}

/**
 * 计算可交互时间 (TTI)
 */
export function calculateTTI(): number {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (!navigation) return 0;
  
  // 简化的TTI计算：DOM内容加载完成时间 + 总阻塞时间
  const domContentLoaded = navigation.domContentLoadedEventEnd;
  const tbt = calculateTBT();
  
  return domContentLoaded + tbt;
}

/**
 * 获取Web Vitals指标
 */
export function getWebVitals(): Partial<WebVitals> {
  const vitals: Partial<WebVitals> = {};
  
  // 获取FCP
  const fcpEntry = performance.getEntriesByName('first-contentful-paint')[0];
  if (fcpEntry) {
    vitals.fcp = fcpEntry.startTime;
  }
  
  // 获取LCP
  const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
  if (lcpEntries.length > 0) {
    const lastLCP = lcpEntries[lcpEntries.length - 1];
    vitals.lcp = lastLCP.startTime;
  }
  
  // 计算TBT
  vitals.tbt = calculateTBT();
  
  // 计算TTI
  vitals.tti = calculateTTI();
  
  return vitals;
}

/**
 * 获取完整的性能指标
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  const metrics: PerformanceMetrics = {};
  
  // 获取Web Vitals
  const vitals = getWebVitals();
  metrics.fcp = vitals.fcp;
  metrics.lcp = vitals.lcp;
  metrics.tbt = vitals.tbt;
  metrics.tti = vitals.tti;
  
  // 获取内存信息
  const memory = getMemoryInfo();
  if (memory) {
    metrics.memory = memory;
  }
  
  // 获取页面加载时间
  metrics.loadTime = getLoadTime();
  metrics.domContentLoaded = getDOMContentLoadedTime();
  
  return metrics;
}

/**
 * 获取能耗信息
 */
export function getPowerConsumptionInfo(): PowerConsumptionInfo {
  const info: PowerConsumptionInfo = {
    powerLevel: 'medium',
    powerScore: 50
  };

  // 检查是否支持电池API
  if ('getBattery' in navigator) {
    // 这里只是基础实现，实际使用时需要通过PowerMonitor类获取
    info.powerLevel = 'medium';
    info.powerScore = 50;
  }

  return info;
}

/**
 * 监听性能指标变化
 */
export function observePerformanceMetrics(callback: (metrics: PerformanceMetrics) => void): void {
  // 监听LCP
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          const metrics = getPerformanceMetrics();
          callback(metrics);
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('PerformanceObserver not supported:', e);
    }
  }
} 